import { apiRequest, ApiError } from './api'

export interface PayloadUser {
  id: string
  email: string
  name?: string
  role?: string
  firstName?: string
  lastName?: string
  fullName?: string
  isAdmin?: boolean
  /** Роли доступа (ТЗ «Роли и права доступа»): okp | kontur | coordinator | head */
  roles?: string[]
  mangoExtension?: string | null
  avatar?: { url: string } | null
}

/**
 * Политика блокировки входа задаётся на бэкенде в `src/collections/Users.ts` → `auth`.
 * Значения там не заданы, поэтому действуют умолчания Payload: 5 неудачных попыток
 * (`maxLoginAttempts`) и 10 минут блокировки (`lockTime = 600000`). Меняем политику там —
 * правим числа и здесь, чтобы подсказка на форме входа не врала.
 */
export const MAX_LOGIN_ATTEMPTS = 5
export const LOGIN_LOCK_MINUTES = 10

/** Ответ Payload на попытку входа в заблокированный аккаунт (LockedAuth). */
function isLockedError(message: string): boolean {
  return /too many failed login attempts|locked due/i.test(message)
}

export async function login(email: string, password: string): Promise<PayloadUser> {
  try {
    const data = await apiRequest<{ user: PayloadUser; token: string }>(
      '/api/users/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
      // 401 здесь — это неверный пароль или блокировка, а не протухшая сессия:
      // обновлять токен и уводить на /admin/login не нужно.
      { skipAuthRetry: true },
    )
    return data.user
  } catch (err) {
    throw new Error(loginErrorMessage(err))
  }
}

/** Человеческое объяснение неудачного входа: серверные строки Payload — английские и невнятные. */
export function loginErrorMessage(err: unknown): string {
  const status = err instanceof ApiError ? err.status : 0
  const server = err instanceof ApiError ? err.serverMessage : ((err as Error)?.message ?? '')

  if (isLockedError(server) || (status === 401 && /lock/i.test(server))) {
    return (
      `Вход временно заблокирован: превышено число неудачных попыток ввода пароля (${MAX_LOGIN_ATTEMPTS}). ` +
      `Повторите попытку через ${LOGIN_LOCK_MINUTES} минут или попросите администратора разблокировать вас ` +
      `— Настройки → Пользователи → «Разблокировать».`
    )
  }
  if (status === 401) {
    return (
      `Неверный email или пароль. Внимание: после ${MAX_LOGIN_ATTEMPTS} неудачных попыток вход в аккаунт ` +
      `блокируется на ${LOGIN_LOCK_MINUTES} минут.`
    )
  }
  if (status === 403) return 'Вход с этого адреса запрещён. Обратитесь к администратору.'
  if (status === 400) return 'Проверьте email и пароль — поля заполнены неверно.'
  return server || 'Не удалось войти. Попробуйте ещё раз или обратитесь к администратору.'
}

export async function logout() {
  await apiRequest('/api/users/logout', { method: 'POST' })
}

export async function getMe(): Promise<PayloadUser | null> {
  try {
    const data = await apiRequest<{ user: PayloadUser }>('/api/users/me')
    return data.user
  } catch {
    return null
  }
}
